import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i2 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, PLATFORM_ID, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, Input, Output, ContentChildren, NgModule } from '@angular/core';
import * as i1 from 'primeng/api';
import { PrimeTemplate, SharedModule } from 'primeng/api';
import { DomHandler, ConnectedOverlayScrollHandler } from 'primeng/dom';
import { TimesIcon } from 'primeng/icons/times';
import * as i3 from 'primeng/ripple';
import { RippleModule } from 'primeng/ripple';
import { ZIndexUtils } from 'primeng/utils';

/**
 * OverlayPanel is a container component positioned as connected to its target.
 * @group Components
 */
function OverlayPanel_div_0_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function OverlayPanel_div_0_button_4_TimesIcon_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "TimesIcon", 8);
  }
  if (rf & 2) {
    i0.ɵɵproperty("styleClass", "p-overlaypanel-close-icon");
  }
}
function OverlayPanel_div_0_button_4_span_2_1_ng_template_0_Template(rf, ctx) {}
function OverlayPanel_div_0_button_4_span_2_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, OverlayPanel_div_0_button_4_span_2_1_ng_template_0_Template, 0, 0, "ng-template");
  }
}
function OverlayPanel_div_0_button_4_span_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 9);
    i0.ɵɵtemplate(1, OverlayPanel_div_0_button_4_span_2_1_Template, 1, 0, null, 3);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r4.closeIconTemplate);
  }
}
function OverlayPanel_div_0_button_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 5);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_button_4_Template_button_click_0_listener($event) {
      i0.ɵɵrestoreView(_r8);
      const ctx_r7 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r7.onCloseClick($event));
    })("keydown.enter", function OverlayPanel_div_0_button_4_Template_button_keydown_enter_0_listener() {
      i0.ɵɵrestoreView(_r8);
      const ctx_r9 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r9.hide());
    });
    i0.ɵɵtemplate(1, OverlayPanel_div_0_button_4_TimesIcon_1_Template, 1, 1, "TimesIcon", 6);
    i0.ɵɵtemplate(2, OverlayPanel_div_0_button_4_span_2_Template, 2, 1, "span", 7);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵattribute("aria-label", ctx_r2.ariaCloseLabel);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r2.closeIconTemplate);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.closeIconTemplate);
  }
}
const _c0 = function (a0, a1) {
  return {
    showTransitionParams: a0,
    hideTransitionParams: a1
  };
};
const _c1 = function (a0, a1) {
  return {
    value: a0,
    params: a1
  };
};
function OverlayPanel_div_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_Template_div_click_0_listener($event) {
      i0.ɵɵrestoreView(_r11);
      const ctx_r10 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r10.onOverlayClick($event));
    })("@animation.start", function OverlayPanel_div_0_Template_div_animation_animation_start_0_listener($event) {
      i0.ɵɵrestoreView(_r11);
      const ctx_r12 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r12.onAnimationStart($event));
    })("@animation.done", function OverlayPanel_div_0_Template_div_animation_animation_done_0_listener($event) {
      i0.ɵɵrestoreView(_r11);
      const ctx_r13 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r13.onAnimationEnd($event));
    });
    i0.ɵɵelementStart(1, "div", 2);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_Template_div_click_1_listener() {
      i0.ɵɵrestoreView(_r11);
      const ctx_r14 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r14.onContentClick());
    })("mousedown", function OverlayPanel_div_0_Template_div_mousedown_1_listener() {
      i0.ɵɵrestoreView(_r11);
      const ctx_r15 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r15.onContentClick());
    });
    i0.ɵɵprojection(2);
    i0.ɵɵtemplate(3, OverlayPanel_div_0_ng_container_3_Template, 1, 0, "ng-container", 3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, OverlayPanel_div_0_button_4_Template, 3, 3, "button", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(ctx_r0.styleClass);
    i0.ɵɵproperty("ngClass", "p-overlaypanel p-component")("ngStyle", ctx_r0.style)("@animation", i0.ɵɵpureFunction2(11, _c1, ctx_r0.overlayVisible ? "open" : "close", i0.ɵɵpureFunction2(8, _c0, ctx_r0.showTransitionOptions, ctx_r0.hideTransitionOptions)));
    i0.ɵɵattribute("aria-modal", ctx_r0.overlayVisible);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentTemplate);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.showCloseIcon);
  }
}
const _c2 = ["*"];
let OverlayPanel = /*#__PURE__*/(() => {
  class OverlayPanel {
    document;
    platformId;
    el;
    renderer;
    cd;
    zone;
    config;
    overlayService;
    /**
     * Enables to hide the overlay when outside is clicked.
     * @group Props
     */
    dismissable = true;
    /**
     * When enabled, displays a close icon at top right corner.
     * @group Props
     */
    showCloseIcon;
    /**
     * Inline style of the component.
     * @group Props
     */
    style;
    /**
     * Style class of the component.
     * @group Props
     */
    styleClass;
    /**
     *  Target element to attach the panel, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
     * @group Props
     */
    appendTo = 'body';
    /**
     * Whether to automatically manage layering.
     * @group Props
     */
    autoZIndex = true;
    /**
     * Aria label of the close icon.
     * @group Props
     */
    ariaCloseLabel;
    /**
     * Base zIndex value to use in layering.
     * @group Props
     */
    baseZIndex = 0;
    /**
     * When enabled, first button receives focus on show.
     * @group Props
     */
    focusOnShow = true;
    /**
     * Transition options of the show animation.
     * @group Props
     */
    showTransitionOptions = '.12s cubic-bezier(0, 0, 0.2, 1)';
    /**
     * Transition options of the hide animation.
     * @group Props
     */
    hideTransitionOptions = '.1s linear';
    /**
     * Callback to invoke when an overlay becomes visible.
     * @group Emits
     */
    onShow = new EventEmitter();
    /**
     * Callback to invoke when an overlay gets hidden.
     * @group Emits
     */
    onHide = new EventEmitter();
    templates;
    container;
    overlayVisible = false;
    render = false;
    isOverlayAnimationInProgress = false;
    selfClick = false;
    documentClickListener;
    target;
    willHide;
    scrollHandler;
    documentResizeListener;
    contentTemplate;
    closeIconTemplate;
    destroyCallback;
    overlayEventListener;
    overlaySubscription;
    constructor(document, platformId, el, renderer, cd, zone, config, overlayService) {
      this.document = document;
      this.platformId = platformId;
      this.el = el;
      this.renderer = renderer;
      this.cd = cd;
      this.zone = zone;
      this.config = config;
      this.overlayService = overlayService;
    }
    ngAfterContentInit() {
      this.templates?.forEach(item => {
        switch (item.getType()) {
          case 'content':
            this.contentTemplate = item.template;
            break;
          case 'closeicon':
            this.closeIconTemplate = item.template;
            break;
          default:
            this.contentTemplate = item.template;
            break;
        }
        this.cd.markForCheck();
      });
    }
    bindDocumentClickListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.documentClickListener && this.dismissable) {
          let documentEvent = DomHandler.isIOS() ? 'touchstart' : 'click';
          const documentTarget = this.el ? this.el.nativeElement.ownerDocument : this.document;
          this.documentClickListener = this.renderer.listen(documentTarget, documentEvent, event => {
            if (!this.container?.contains(event.target) && !this.target.contains(event.target)) {
              this.hide();
            }
            this.cd.markForCheck();
          });
        }
      }
    }
    unbindDocumentClickListener() {
      if (this.documentClickListener) {
        this.documentClickListener();
        this.documentClickListener = null;
        this.selfClick = false;
      }
    }
    /**
     * Toggles the visibility of the panel.
     * @param {Event} event - Browser event
     * @param {Target} target - Target element.
     * @group Method
     */
    toggle(event, target) {
      if (this.isOverlayAnimationInProgress) {
        return;
      }
      if (this.overlayVisible) {
        if (this.hasTargetChanged(event, target)) {
          this.destroyCallback = () => {
            this.show(null, target || event.currentTarget || event.target);
          };
        }
        this.hide();
      } else {
        this.show(event, target);
      }
    }
    /**
     * Displays the panel.
     * @param {Event} event - Browser event
     * @param {Target} target - Target element.
     * @group Method
     */
    show(event, target) {
      target && event && event.stopPropagation();
      if (this.isOverlayAnimationInProgress) {
        return;
      }
      this.target = target || event.currentTarget || event.target;
      this.overlayVisible = true;
      this.render = true;
      this.cd.markForCheck();
    }
    onOverlayClick(event) {
      this.overlayService.add({
        originalEvent: event,
        target: this.el.nativeElement
      });
      this.selfClick = true;
    }
    onContentClick() {
      this.selfClick = true;
    }
    hasTargetChanged(event, target) {
      return this.target != null && this.target !== (target || event.currentTarget || event.target);
    }
    appendContainer() {
      if (this.appendTo) {
        if (this.appendTo === 'body') this.renderer.appendChild(this.document.body, this.container);else DomHandler.appendChild(this.container, this.appendTo);
      }
    }
    restoreAppend() {
      if (this.container && this.appendTo) {
        this.renderer.appendChild(this.el.nativeElement, this.container);
      }
    }
    align() {
      if (this.autoZIndex) {
        ZIndexUtils.set('overlay', this.container, this.baseZIndex + this.config.zIndex.overlay);
      }
      DomHandler.absolutePosition(this.container, this.target);
      const containerOffset = DomHandler.getOffset(this.container);
      const targetOffset = DomHandler.getOffset(this.target);
      const borderRadius = this.document.defaultView?.getComputedStyle(this.container).getPropertyValue('border-radius');
      let arrowLeft = 0;
      if (containerOffset.left < targetOffset.left) {
        arrowLeft = targetOffset.left - containerOffset.left - parseFloat(borderRadius) * 2;
      }
      this.container?.style.setProperty('--overlayArrowLeft', `${arrowLeft}px`);
      if (containerOffset.top < targetOffset.top) {
        DomHandler.addClass(this.container, 'p-overlaypanel-flipped');
        if (this.showCloseIcon) {
          this.renderer.setStyle(this.container, 'margin-top', '-30px');
        }
      }
    }
    onAnimationStart(event) {
      if (event.toState === 'open') {
        this.container = event.element;
        this.appendContainer();
        this.align();
        this.bindDocumentClickListener();
        this.bindDocumentResizeListener();
        this.bindScrollListener();
        if (this.focusOnShow) {
          this.focus();
        }
        this.overlayEventListener = e => {
          if (this.container && this.container.contains(e.target)) {
            this.selfClick = true;
          }
        };
        this.overlaySubscription = this.overlayService.clickObservable.subscribe(this.overlayEventListener);
        this.onShow.emit(null);
      }
      this.isOverlayAnimationInProgress = true;
    }
    onAnimationEnd(event) {
      switch (event.toState) {
        case 'void':
          if (this.destroyCallback) {
            this.destroyCallback();
            this.destroyCallback = null;
          }
          if (this.overlaySubscription) {
            this.overlaySubscription.unsubscribe();
          }
          break;
        case 'close':
          if (this.autoZIndex) {
            ZIndexUtils.clear(this.container);
          }
          if (this.overlaySubscription) {
            this.overlaySubscription.unsubscribe();
          }
          this.onContainerDestroy();
          this.onHide.emit({});
          this.render = false;
          break;
      }
      this.isOverlayAnimationInProgress = false;
    }
    focus() {
      let focusable = DomHandler.findSingle(this.container, '[autofocus]');
      if (focusable) {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => focusable.focus(), 5);
        });
      }
    }
    /**
     * Hides the panel.
     * @group Method
     */
    hide() {
      this.overlayVisible = false;
      this.cd.markForCheck();
    }
    onCloseClick(event) {
      this.hide();
      event.preventDefault();
    }
    onWindowResize() {
      if (this.overlayVisible && !DomHandler.isTouchDevice()) {
        this.hide();
      }
    }
    bindDocumentResizeListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.documentResizeListener) {
          const window = this.document.defaultView;
          this.documentResizeListener = this.renderer.listen(window, 'resize', this.onWindowResize.bind(this));
        }
      }
    }
    unbindDocumentResizeListener() {
      if (this.documentResizeListener) {
        this.documentResizeListener();
        this.documentResizeListener = null;
      }
    }
    bindScrollListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.scrollHandler) {
          this.scrollHandler = new ConnectedOverlayScrollHandler(this.target, () => {
            if (this.overlayVisible) {
              this.hide();
            }
          });
        }
        this.scrollHandler.bindScrollListener();
      }
    }
    unbindScrollListener() {
      if (this.scrollHandler) {
        this.scrollHandler.unbindScrollListener();
      }
    }
    onContainerDestroy() {
      if (!this.cd.destroyed) {
        this.target = null;
      }
      this.unbindDocumentClickListener();
      this.unbindDocumentResizeListener();
      this.unbindScrollListener();
    }
    ngOnDestroy() {
      if (this.scrollHandler) {
        this.scrollHandler.destroy();
        this.scrollHandler = null;
      }
      if (this.container && this.autoZIndex) {
        ZIndexUtils.clear(this.container);
      }
      if (!this.cd.destroyed) {
        this.target = null;
      }
      this.destroyCallback = null;
      if (this.container) {
        this.restoreAppend();
        this.onContainerDestroy();
      }
      if (this.overlaySubscription) {
        this.overlaySubscription.unsubscribe();
      }
    }
    static ɵfac = function OverlayPanel_Factory(t) {
      return new (t || OverlayPanel)(i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.PrimeNGConfig), i0.ɵɵdirectiveInject(i1.OverlayService));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: OverlayPanel,
      selectors: [["p-overlayPanel"]],
      contentQueries: function OverlayPanel_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, PrimeTemplate, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templates = _t);
        }
      },
      hostAttrs: [1, "p-element"],
      inputs: {
        dismissable: "dismissable",
        showCloseIcon: "showCloseIcon",
        style: "style",
        styleClass: "styleClass",
        appendTo: "appendTo",
        autoZIndex: "autoZIndex",
        ariaCloseLabel: "ariaCloseLabel",
        baseZIndex: "baseZIndex",
        focusOnShow: "focusOnShow",
        showTransitionOptions: "showTransitionOptions",
        hideTransitionOptions: "hideTransitionOptions"
      },
      outputs: {
        onShow: "onShow",
        onHide: "onHide"
      },
      ngContentSelectors: _c2,
      decls: 1,
      vars: 1,
      consts: [["role", "dialog", 3, "ngClass", "ngStyle", "class", "click", 4, "ngIf"], ["role", "dialog", 3, "ngClass", "ngStyle", "click"], [1, "p-overlaypanel-content", 3, "click", "mousedown"], [4, "ngTemplateOutlet"], ["type", "button", "class", "p-overlaypanel-close p-link", "pRipple", "", 3, "click", "keydown.enter", 4, "ngIf"], ["type", "button", "pRipple", "", 1, "p-overlaypanel-close", "p-link", 3, "click", "keydown.enter"], [3, "styleClass", 4, "ngIf"], ["class", "p-overlaypanel-close-icon", 4, "ngIf"], [3, "styleClass"], [1, "p-overlaypanel-close-icon"]],
      template: function OverlayPanel_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, OverlayPanel_div_0_Template, 5, 14, "div", 0);
        }
        if (rf & 2) {
          i0.ɵɵproperty("ngIf", ctx.render);
        }
      },
      dependencies: function () {
        return [i2.NgClass, i2.NgIf, i2.NgTemplateOutlet, i2.NgStyle, i3.Ripple, TimesIcon];
      },
      styles: ["@layer primeng{.p-overlaypanel{position:absolute;margin-top:10px;top:0;left:0}.p-overlaypanel-flipped{margin-top:0;margin-bottom:10px}.p-overlaypanel-close{display:flex;justify-content:center;align-items:center;overflow:hidden;position:relative}.p-overlaypanel:after,.p-overlaypanel:before{bottom:100%;left:calc(var(--overlayArrowLeft, 0) + 1.25rem);content:\" \";height:0;width:0;position:absolute;pointer-events:none}.p-overlaypanel:after{border-width:8px;margin-left:-8px}.p-overlaypanel:before{border-width:10px;margin-left:-10px}.p-overlaypanel-shifted:after,.p-overlaypanel-shifted:before{left:auto;right:1.25em;margin-left:auto}.p-overlaypanel-flipped:after,.p-overlaypanel-flipped:before{bottom:auto;top:100%}.p-overlaypanel.p-overlaypanel-flipped:after{border-bottom-color:transparent}.p-overlaypanel.p-overlaypanel-flipped:before{border-bottom-color:transparent}}\n"],
      encapsulation: 2,
      data: {
        animation: [trigger('animation', [state('void', style({
          transform: 'scaleY(0.8)',
          opacity: 0
        })), state('close', style({
          opacity: 0
        })), state('open', style({
          transform: 'translateY(0)',
          opacity: 1
        })), transition('void => open', animate('{{showTransitionParams}}')), transition('open => close', animate('{{hideTransitionParams}}'))])]
      },
      changeDetection: 0
    });
  }
  return OverlayPanel;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let OverlayPanelModule = /*#__PURE__*/(() => {
  class OverlayPanelModule {
    static ɵfac = function OverlayPanelModule_Factory(t) {
      return new (t || OverlayPanelModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: OverlayPanelModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, RippleModule, SharedModule, TimesIcon, SharedModule]
    });
  }
  return OverlayPanelModule;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { OverlayPanel, OverlayPanelModule };
